import { useState, useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Trash2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeleteAccountPage() {
  const [form, setForm] = useState({ email: "", reason: "" });
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setState("loading");
    try {
      await axios.post(`${API}/delete-account-request`, form);
      setState("success");
      setMsg("Your deletion request has been received. We'll process it within 30 days and confirm by email.");
      setForm({ email: "", reason: "" });
    } catch {
      setState("error");
      setMsg("Something went wrong. Please email hello@neuronest.global directly.");
      setTimeout(() => setState("idle"), 4000);
    }
  };

  return (
    <div data-testid="delete-account-page" className="min-h-screen bg-[#F8F9FB]">
      <SEO
        title="Delete Your Account"
        description="Request deletion of your NeuroNest account and all associated data. We'll process your request within 30 days."
        path="/delete-account"
      />
      <Navbar />

      <main className="pt-28 pb-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h1
              data-testid="delete-title"
              className="font-heading text-3xl md:text-4xl font-bold text-[#2F3A56] leading-tight mb-4"
            >
              Delete Your Account
            </h1>
            <p className="text-base text-[#5A6376] leading-relaxed max-w-lg mx-auto">
              We're sorry to see you go. Submitting this form will permanently
              delete your NeuroNest account and all associated data within 30
              days.
            </p>
          </div>

          <div className="bg-white rounded-3xl border border-[#E7E9EC] shadow-[0_4px_20px_rgba(0,0,0,0.04)] p-8">
            <div className="flex items-start gap-3 bg-brand-rose/10 rounded-2xl p-4 mb-6">
              <Trash2 size={18} className="text-brand-rose flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-semibold text-[#2F3A56] mb-1">What will be deleted:</p>
                <ul className="text-xs text-[#5A6376] space-y-1">
                  <li>Your account and login credentials</li>
                  <li>All check-in data, routines, and communication cards</li>
                  <li>Your sensory profile and safe places</li>
                  <li>All Sensory Coach conversation history</li>
                  <li>Any child accounts linked to your profile</li>
                  <li>Family Circle connections</li>
                </ul>
              </div>
            </div>

            {state === "success" ? (
              <div
                data-testid="delete-success"
                className="flex items-center gap-3 justify-center bg-brand-sage/20 rounded-2xl p-6"
              >
                <CheckCircle2 size={20} className="text-[#7EAD87]" />
                <span className="text-sm font-medium text-[#2F3A56]">{msg}</span>
              </div>
            ) : (
              <form data-testid="delete-account-form" onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-xs font-medium text-[#2F3A56] mb-1.5">
                    Email address associated with your account *
                  </label>
                  <input
                    data-testid="delete-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full rounded-xl border border-[#E7E9EC] bg-[#F8F9FB] px-4 py-3 text-sm text-[#2F3A56] placeholder:text-[#8B95A5] focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal transition-all"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#2F3A56] mb-1.5">
                    Reason for leaving (optional)
                  </label>
                  <textarea
                    data-testid="delete-reason"
                    rows={3}
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    className="w-full rounded-xl border border-[#E7E9EC] bg-[#F8F9FB] px-4 py-3 text-sm text-[#2F3A56] placeholder:text-[#8B95A5] focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal transition-all resize-none"
                    placeholder="We'd appreciate any feedback, but this is entirely optional"
                  />
                </div>
                {state === "error" && (
                  <p className="text-xs text-brand-rose">{msg}</p>
                )}
                <Button
                  data-testid="delete-submit"
                  type="submit"
                  disabled={state === "loading"}
                  className="rounded-full bg-brand-rose hover:bg-brand-rose/90 text-white px-8 py-3 text-sm font-semibold h-auto w-full disabled:opacity-70"
                >
                  {state === "loading" ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    "Request Account Deletion"
                  )}
                </Button>
              </form>
            )}

            <p className="text-xs text-[#8B95A5] text-center mt-6">
              You can also email{" "}
              <a href="mailto:hello@neuronest.global" className="text-brand-teal hover:underline">
                hello@neuronest.global
              </a>{" "}
              to request deletion.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
